import { useEffect, useMemo, useState } from "react";
import CryptoJS from "crypto-js";
import { useNavigate, useLocation } from "react-router-dom";
import alertUtils from "../utils/utils";

function PasswordChangePage() {
    const navigate = useNavigate();
    const location = useLocation();

    const [userId, setUserId] = useState("");

    const [currentPwd, setCurrentPwd] = useState("");
    const [newPwd, setNewPwd] = useState("");
    const [newPwdChk, setNewPwdChk] = useState("");

    const [encryptInfo, setEncryptInfo] = useState({
        key: "",
        iv: ""
    });

    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (location.state?.userId) {
            setUserId(location.state.userId);
        }
    }, [location.state]);

    const passwordRule = useMemo(() => {
        const num = newPwd.search(/[0-9]/g);
        const eng = newPwd.search(/[a-z]/ig);
        const spe = newPwd.search(/[{}[\]/?.,;:|)*~`!^_+<>@#$%&\\=('"-]/g);

        return {
            lengthValid: newPwd.length >= 8 && newPwd.length <= 15,
            noSpace: newPwd.search(/\s/) === -1,
            mixValid: num >= 0 && eng >= 0 && spe >= 0,
            noRepeat4: !/(\w)\1\1\1/.test(newPwd),
            noUserId: userId ? newPwd.search(userId) === -1 : true
        };
    }, [newPwd, userId]);

    const isEqualInvalid =
        newPwd !== "" &&
        newPwdChk !== "" &&
        newPwd !== newPwdChk;

    const resetForm = () => {
        setCurrentPwd("");
        setNewPwd("");
        setNewPwdChk("");
    };

    const showWarning = (message) => {
        alertUtils.warning(message);
    };

    const showError = (message) => {
        alertUtils.error(message);
    };

    const showSuccess = (message) => {
        alertUtils.successWithCallback(message, () => navigate("/"));
    };

    const chkPassword = (id, pw) => {
        const num = pw.search(/[0-9]/g);
        const eng = pw.search(/[a-z]/ig);
        const spe = pw.search(/[{}[\]/?.,;:|)*~`!^_+<>@#$%&\\=('"-]/g);

        if (pw.length < 8 || pw.length > 15) {
            showWarning("8자리 ~ 15자리 이내로 입력해주세요.");
            return false;
        }

        if (pw.search(/\s/) !== -1) {
            showWarning("비밀번호는 공백 없이 입력해주세요.");
            return false;
        }

        if (num < 0 || eng < 0 || spe < 0) {
            showWarning("영문, 숫자, 특수문자를 혼합하여 입력해주세요.");
            return false;
        }

        if (/(\w)\1\1\1/.test(pw)) {
            showWarning("같은 문자를 4번 이상 사용하실 수 없습니다.");
            return false;
        }

        if (id && pw.search(id) > -1) {
            showWarning("비밀번호에 아이디가 포함되었습니다.");
            return false;
        }

        return true;
    };

    const getEncryptKey = async () => {
        if (encryptInfo.key && encryptInfo.iv) {
            return encryptInfo;
        }

        const response = await fetch(`${(window.__env__?.REACT_APP_API_BASE_URL ?? process.env.REACT_APP_API_BASE_URL)}/adm/encrypt/key`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json"
            }
        });

        if (!response.ok) {
            throw new Error("암호화 키 조회에 실패했습니다.");
        }

        const data = await response.json();

        const nextEncryptInfo = {
            key: data.key || "",
            iv: data.iv || ""
        };

        setEncryptInfo(nextEncryptInfo);
        return nextEncryptInfo;
    };

    const encodePassword = async (pw) => {
        const { key, iv } = await getEncryptKey();

        const cipher = CryptoJS.AES.encrypt(
            pw,
            CryptoJS.enc.Hex.parse(key),
            {
                iv: CryptoJS.enc.Hex.parse(iv),
                padding: CryptoJS.pad.Pkcs7,
                mode: CryptoJS.mode.CBC
            }
        );

        return cipher.toString();
    };

    const handleSave = async () => {
        if (!userId) {
            showWarning("사용자 정보가 없습니다.");
            return;
        }

        if (currentPwd.length < 1) {
            showWarning("비밀번호를 입력해주세요.");
            return;
        }

        if (newPwd.length < 1) {
            showWarning("변경할 비밀번호를 입력해주세요.");
            return;
        }

        if (currentPwd === newPwd) {
            showWarning("기존비밀번호와 같습니다.");
            return;
        }

        if (newPwd !== newPwdChk) {
            showWarning("신규 비밀번호가 일치하지 않습니다.");
            return;
        }

        if (!chkPassword(userId, newPwd)) {
            return;
        }

        try {
            setIsSaving(true);

            const encodedPw0 = await encodePassword(currentPwd);
            const encodedPw1 = await encodePassword(newPwd);
            const encodedPw2 = await encodePassword(newPwdChk);

            const response = await fetch(`${(window.__env__?.REACT_APP_API_BASE_URL ?? process.env.REACT_APP_API_BASE_URL)}/adm/main/pwd/update`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json; charset=utf-8"
                },
                body: JSON.stringify({
                    id: userId,
                    pw0: encodedPw0,
                    pw1: encodedPw1,
                    pw2: encodedPw2
                })
            });

            if (response.status === 200) {
                showSuccess("비밀번호 변경 완료. 로그인페이지로 이동합니다.");
            }

            else if (response.status === 400) {
                showWarning("비밀번호 5회 실패로 계정이 잠겼습니다. 관리자에게 잠금해제 요청하세요.");
                return;
            }

            else if (response.status === 404) {
                showWarning("현재 비밀번호가 올바르지 않습니다.");
                return;
            }

            else if (response.status === 422) {
                showWarning("이전 비밀번호와 같습니다.");
                return;
            }
            else {
                showError("비밀번호 변경 중 오류가 발생했습니다.");
            }
        } catch (error) {
            console.error(error);
            showError("비밀번호 변경 중 오류가 발생했습니다.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        resetForm();
        navigate(-1);
    };

    return (
        <div className="password-page">
            <div className="password-page__inner">
                <div className="password-card">
                    <div className="password-card__header">
                        <h2 className="password-card__title">비밀번호 변경</h2>
                        <p className="password-card__desc">
                            안전한 계정 사용을 위해 비밀번호를 변경해주세요.
                        </p>
                    </div>

                    <div className="password-form">
                        <div className="form-row">
                            <label className="form-label" htmlFor="userId">
                                아이디
                            </label>
                            <input
                                id="userId"
                                className="form-input"
                                type="text"
                                value={userId}
                                readOnly
                            />
                        </div>

                        <div className="form-row">
                            <label className="form-label" htmlFor="currentPwd">
                                현재 비밀번호
                            </label>
                            <input
                                id="currentPwd"
                                className="form-input"
                                type="password"
                                autoComplete="new-password"
                                value={currentPwd}
                                onChange={(e) => setCurrentPwd(e.target.value)}
                            />
                        </div>

                        <div className="form-row">
                            <label className="form-label" htmlFor="newPwd">
                                새 비밀번호
                            </label>
                            <input
                                id="newPwd"
                                className="form-input"
                                type="password"
                                autoComplete="new-password"
                                value={newPwd}
                                onChange={(e) => setNewPwd(e.target.value)}
                            />

                            <div className="password-rules">
                                <p className={`rule-text ${passwordRule.lengthValid ? "" : "is-invalid"}`}>
                                    * 8자 이상 15자 이하
                                </p>
                                <p className={`rule-text ${passwordRule.noSpace ? "" : "is-invalid"}`}>
                                    * 공백 불가능
                                </p>
                                <p className={`rule-text ${passwordRule.mixValid ? "" : "is-invalid"}`}>
                                    * 영문/숫자/특수문자 혼합
                                </p>
                                <p className={`rule-text ${passwordRule.noRepeat4 ? "" : "is-invalid"}`}>
                                    * 같은 문자 4번 연속 불가능
                                </p>
                                <p className={`rule-text ${passwordRule.noUserId ? "" : "is-invalid"}`}>
                                    * 아이디 포함 불가능
                                </p>
                            </div>
                        </div>

                        <div className="form-row">
                            <label className="form-label" htmlFor="newPwdChk">
                                새 비밀번호 확인
                            </label>
                            <input
                                id="newPwdChk"
                                className="form-input"
                                type="password"
                                autoComplete="new-password"
                                value={newPwdChk}
                                onChange={(e) => setNewPwdChk(e.target.value)}
                            />
                            {isEqualInvalid && (
                                <p className="input-error">
                                    * 비밀번호가 일치하지 않습니다.
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="password-card__footer">
                        <button
                            type="button"
                            className="btn btn-gray"
                            onClick={handleCancel}
                            disabled={isSaving}
                        >
                            취소
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSave}
                            disabled={isSaving}
                        >
                            {isSaving ? "저장 중..." : "저장"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PasswordChangePage;